import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { z } from 'zod';

type ValidatableData = Record<string, unknown>;

// Compute nested dot-path keys for an object type T
type NestedKeyOf<T> = T extends object
  ? {
      [K in keyof T & string]: NonNullable<T[K]> extends object
        ? `${K}` | `${K}.${NestedKeyOf<NonNullable<T[K]>>}`
        : `${K}`;
    }[keyof T & string]
  : never;

/**
 * A hook for validating data using Zod schemas.
 * Supports validating any object against any schema, while maintaining type safety.
 *
 * @param data The data to validate
 * @param schema The Zod schema to validate against
 * @returns Object with utility functions for validation
 */
export const useErrorValidator = <T extends ValidatableData, S extends z.ZodType<unknown>>(data: T, schema: S) => {
  const t = useTranslations();

  const parsedSchema = useMemo(() => schema.safeParse(data), [schema, data]);

  // Define type for Zod error structure
  type FieldErrors = Record<string, string[]>;

  // Build an error map keyed by dot-joined paths, e.g., "venue.address"
  const rawErrors: FieldErrors = useMemo(() => {
    const errors: FieldErrors = {};
    if (!parsedSchema.success) {
      for (const issue of parsedSchema.error.issues) {
        const pathKey = issue.path && issue.path.length > 0 ? issue.path.join('.') : '_root';
        if (!errors[pathKey]) errors[pathKey] = [];
        errors[pathKey].push(issue.message);
      }
    }
    return errors;
  }, [parsedSchema]);

  // Retrieve the first translated error message for a given field path
  const getError = <K extends NestedKeyOf<T>>(field: K): string | undefined => {
    const errors = rawErrors[field as string];
    if (!errors || errors.length === 0) return undefined;

    const errorMessage = errors[0];
    if (!errorMessage) return undefined;

    // Some Zod issues use plain strings (e.g. "Required"). Avoid throwing when a key is missing.
    try {
      return t(errorMessage as never);
    } catch {
      return errorMessage;
    }
  };

  return {
    getError,
    isValid: parsedSchema.success,
    errors: rawErrors,
  };
};
